import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Sparkles, HelpCircle, RefreshCw, Wand2, X, Check } from "lucide-react";
import { cn } from "../../lib/cn";
import { BlockReceiptView, readReceipt, type BlockReceipt } from "./blockReceipt";
import { BLOCK_SURFACE_PADDING } from "./blockSurface";

/**
 * BetterAnswer - the post-answer "I can answer this better" nudge, as a shared
 * K2 block. Renderer for the `better_answer` block, raised from the runtime's
 * `better_answer_suggestion` post-answer signal (ais-agent-runtime
 * `events.better_answer_suggestion` -> `PostProcessSignals`,
 * AISquare-Studio/ais-agent-runtime#115). The platform's chat `BlockRenderer`
 * passes the block's `props` straight through, so the prop names match the
 * block payload exactly. INTERACTIVE.
 *
 * This is the reusable replacement for AIStudio-v3's bespoke `BetterAnswerCard`
 * (RT#61): both FE chats (Creator Studio FE-React + AIStudio-v3) render the
 * same block instead of each hand-rolling the card. It is a gentle, optional
 * "I could do better - want me to?" nudge, NOT an error/warning: low-contrast
 * surface, subtle border, no destructive colour. It NEVER alters the delivered
 * answer; accept re-submits `rewrittenQuery` as a normal new turn.
 *
 * Block props (`{ kind, label, rewrittenQuery, submit_to, receipt? }`):
 *   - `kind` - the gap class: `"question"` (re-ask as a proper choice),
 *     `"experience"` (create/publish the experience the intent warranted),
 *     `"rewrite"` (re-run under-grounded with a search-directing query), or a
 *     free string. Drives the leading icon. Required.
 *   - `label` - the human prompt shown on the nudge. Required.
 *   - `rewrittenQuery` - the natural-language query re-submitted as a new turn
 *     on accept. Required. (Wire field: `rewritten_query`; the BlockRenderer
 *     adapter accepts either spelling.)
 *   - `submit_to` - destination routing token (mirrors `form.submit_to`),
 *     carried on the emitted submit event.
 *   - `receipt?` - once acted on, the durable settled state. When present the
 *     settled receipt renders instead of the live nudge, so a history reload
 *     shows the outcome rather than re-offering a live, un-actioned control.
 *
 * On accept the block emits the shared block-submit event keyed by `block_id` +
 * `action: "accept"` + the `rewrittenQuery` as the value (the next turn). On
 * dismiss it emits `action: "dismiss"` with no value. Transport is
 * `blockSubmit.tsx` - the same one `form` / `approval` / `suggestions` use.
 *
 * Client-only props (never part of the streamed block): `className`, `onAccept`
 * / `onDismiss` (wired by the block adapter to the submit context), `disabled`.
 *
 * Token-themed with no hardcoded colors, on the muted-signal variant of the
 * shared block surface (`blockSurface.ts`, #176/#180) - the same treatment as
 * its sibling `MemorySave`. SSR-safe: no `window` / `document` at module top or
 * during render; the entrance is a reduced-motion-safe tailwindcss-animate
 * class, not a JS animation, so the block carries no motion dependency.
 */

/** The gap class the post-answer judge found. Open union. */
export type BetterAnswerKind = "question" | "experience" | "rewrite" | (string & {});

/** Subtle, kind-aware nudge icon. Falls back to `Sparkles` for unknown kinds. */
function iconForKind(kind: string): React.ComponentType<{ className?: string }> {
  switch (kind) {
    case "question":
      return HelpCircle;
    case "experience":
      return Wand2;
    case "rewrite":
      return RefreshCw;
    default:
      return Sparkles;
  }
}

const betterAnswerVariants = cva(
  cn(
    // Muted-signal variant of the shared block surface (blockSurface.ts): same
    // radius + hairline + inset as BLOCK_SURFACE, but a low-emphasis muted
    // fill replaces the card fill - a nudge, not a content card. Mirrors the
    // sibling MemorySave.
    "not-prose flex flex-col gap-2.5 rounded-2xl border border-border/70 bg-muted/40",
    BLOCK_SURFACE_PADDING,
    "sm:flex-row sm:items-center sm:justify-between sm:gap-3",
    // Slide-then-fade entrance, reduced-motion-safe; matches the house chip
    // motion without pulling in a JS animation library.
    "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:fill-mode-both",
  ),
);

const betterAnswerActionVariants = cva(
  cn(
    "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ),
  {
    variants: {
      intent: {
        accept: "border border-border bg-background text-foreground hover:bg-accent/10",
        dismiss: "text-muted-foreground hover:bg-accent/10 hover:text-foreground",
      },
    },
    defaultVariants: { intent: "accept" },
  },
);

export interface BetterAnswerProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof betterAnswerVariants> {
  /** The gap class; drives the leading icon. Required. */
  kind: BetterAnswerKind;
  /** The human prompt shown on the nudge. Required. */
  label: string;
  /** The query re-submitted as a new turn on accept. Required. */
  rewrittenQuery: string;
  /** Accept button copy. Defaults to "Yes, do it". */
  acceptLabel?: string;
  /** Dismiss button copy. Defaults to "Dismiss". */
  dismissLabel?: string;
  /** Settled state - renders the receipt instead of the live nudge when present. */
  receipt?: BlockReceipt | unknown;
  /** Accept handler. Receives `rewrittenQuery`. Wired by the block adapter. */
  onAccept?: (rewrittenQuery: string) => void;
  /** Dismiss handler. Wired by the block adapter to the submit context. */
  onDismiss?: () => void;
  /** Disable interaction (e.g. while a parent settles the suggestion). */
  disabled?: boolean;
}

export const BetterAnswer = React.forwardRef<HTMLDivElement, BetterAnswerProps>(
  function BetterAnswer(
    {
      className,
      kind,
      label,
      rewrittenQuery,
      acceptLabel = "Yes, do it",
      dismissLabel = "Dismiss",
      receipt,
      onAccept,
      onDismiss,
      disabled = false,
      ...rest
    },
    ref,
  ) {
    const settled = readReceipt(receipt);
    // Once Accept is clicked the new turn is being sent and this nudge is on its
    // way out, so freeze both buttons until the host settles / removes the block.
    const [accepting, setAccepting] = React.useState(false);
    const KindIcon = iconForKind(kind);

    const handleAccept = () => {
      if (accepting || disabled) return;
      setAccepting(true);
      onAccept?.(rewrittenQuery);
    };

    if (settled) {
      return (
        <div
          ref={ref}
          className={cn("not-prose", className)}
          data-slot="better-answer"
          data-settled="true"
          {...rest}
        >
          <BlockReceiptView receipt={settled} />
        </div>
      );
    }

    return (
      <div
        ref={ref}
        role="group"
        aria-label={label}
        className={cn(betterAnswerVariants(), className)}
        data-slot="better-answer"
        data-kind={kind}
        {...rest}
      >
        <div className="flex min-w-0 items-start gap-2 sm:items-center">
          <KindIcon
            className="mt-0.5 size-3.5 shrink-0 text-muted-foreground sm:mt-0"
            aria-hidden
          />
          <span className="text-[13px] leading-snug text-muted-foreground">{label}</span>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 self-end sm:self-auto">
          <button
            type="button"
            disabled={disabled || accepting}
            onClick={handleAccept}
            className={betterAnswerActionVariants({ intent: "accept" })}
          >
            <Check className="size-3.5" aria-hidden />
            {accepting ? "Sending..." : acceptLabel}
          </button>
          <button
            type="button"
            disabled={disabled || accepting}
            onClick={() => onDismiss?.()}
            aria-label="Dismiss suggestion"
            className={betterAnswerActionVariants({ intent: "dismiss" })}
          >
            <X className="size-3.5" aria-hidden />
            {dismissLabel}
          </button>
        </div>
      </div>
    );
  },
);

BetterAnswer.displayName = "BetterAnswer";

export { betterAnswerVariants, betterAnswerActionVariants };
export type BetterAnswerVariantProps = VariantProps<typeof betterAnswerVariants>;
